import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const HomePage = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [subscriptionMessage, setSubscriptionMessage] = useState(null);

  const researchFocus = [
    {
      icon: 'fas fa-industry',
      title: t('home.smeSolutions'),
      description: t('home.smeSolutionsDesc')
    },
    {
      icon: 'fas fa-exclamation-triangle',
      title: t('home.hazardIdentification'),
      description: t('home.hazardIdentificationDesc')
    },
    {
      icon: 'fas fa-shield-alt',
      title: t('home.riskPrevention'),
      description: t('home.riskPreventionDesc')
    },
    {
      icon: 'fas fa-brain',
      title: t('home.aiIntegration'),
      description: t('home.aiIntegrationDesc')
    },
  ];

  const handleSubscribe = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      // Replace this with your actual API call
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulated API call
      
      // Simulate successful subscription
      setSubscriptionMessage({
        type: 'success',
        text: 'Thank you for subscribing! Please check your email to confirm.'
      });
      setEmail('');
    } catch (error) {
      setSubscriptionMessage({
        type: 'error',
        text: 'Sorry, there was an error. Please try again later.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-b from-white to-blue-50">
      
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-b from-blue-50 to-white py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-xl md:text-2xl font-medium text-gray-900 mb-6 leading-tight">
              {t('home.heroTitle')}
            </h2>
            <h1 className="text-5xl md:text-8xl font-bold text-gray-900 mb-6 leading-tight">
              {t('home.appName')}
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              {t('home.heroDescription')}
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a href="/tools" className="btn-primary">
                {t('home.exploreTools')}
              </a>
              <a href="/survey" className="btn-secondary">
                {t('home.participateSurvey')}
              </a>
            </div>
          </div>
        </div>
        
        {/* Decorative Elements */}
        <div className="absolute -top-10 -right-10 w-40 h-40 bg-blue-100 rounded-full opacity-20"></div>
        <div className="absolute -bottom-10 -left-10 w-60 h-60 bg-blue-200 rounded-full opacity-20"></div>
      </div>

      {/* Research Focus Section */}
      <div className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-16">{t('home.keyFocusAreas')}</h2>
            <div className="grid md:grid-cols-2 gap-8">
              {researchFocus.map((focus, index) => (
                <div key={index} className="p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                    <i className={`${focus.icon} text-blue-600 text-xl`}></i>
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{focus.title}</h3>
                  <p className="text-gray-600">{focus.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Survey Promotion Section */}
      <div className="py-20 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6">{t('home.surveyPromoTitle')}</h2>
            <p className="text-xl mb-8 opacity-90">
              {t('home.surveyPromoDesc')}
            </p>
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <i className="fas fa-clock text-4xl mb-4"></i>
                <h3 className="text-xl font-semibold mb-2">{t('home.quickProcess')}</h3>
                <p className="opacity-80">{t('home.quickProcessDesc')}</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <i className="fas fa-shield-alt text-4xl mb-4"></i>
                <h3 className="text-xl font-semibold mb-2">{t('home.confidential')}</h3>
                <p className="opacity-80">{t('home.confidentialDesc')}</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <i className="fas fa-chart-line text-4xl mb-4"></i>
                <h3 className="text-xl font-semibold mb-2">{t('home.impact')}</h3>
                <p className="opacity-80">{t('home.impactDesc')}</p>
              </div>
            </div>
            <a href="/survey" className="inline-block px-8 py-4 bg-white text-blue-600 rounded-full font-bold hover:bg-blue-50 transition-colors duration-300 transform hover:scale-105">
              {t('home.takeSurveyNow')}
            </a>
          </div>
        </div>
      </div>

      {/* Contact & Information Section */}
      <div className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto grid md:grid-cols-1 gap-12">
            <div>
              <h2 className="text-3xl font-bold mb-6">{t('home.getInTouch')}</h2>
              <p className="text-gray-600 mb-8">
                {t('home.getInTouchDesc')}
              </p>
              <div className="space-y-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="flex items-start space-x-4">
                  <i className="fas fa-envelope text-blue-600 text-xl w-8"></i>
                  <span className="text-gray-700">{t('home.email')}</span>
                </div>
                <div className="flex items-start space-x-4" style={{margin: 0}}>
                  <i className="fas fa-map-marker-alt text-blue-600 text-xl w-8"></i>
                  <span className="text-gray-700">
                    {t('home.address')}<br/>
                    {t('home.addressLine2')}<br/>
                    {t('home.addressLine3')}
                  </span>
                </div>
              </div>
            </div>
          </div>        </div>
      </div>

    </div>
  );
};

export default HomePage;
